import { Accordion, Badge, Spinner, Text } from "@merge-api/merge-javascript-shared";
import { DiffCategoryTypes, DiffExecutionModelResult } from "../../entities";
import { useGetCommonModelDiffResult } from "../../hooks/useGetCommonModelDiffResult";
import { useEffect, useRef, useState, useCallback, memo } from "react";
import IndividualView from "./IndividualView";
import useDelayedUnmount from "../../hooks/useDelayedUnmount";
import clsx from "clsx";
import ContentLoader from "react-content-loader";

interface ModelDiffViewProps {
  candidateTitle?: string;
  diffRunID: string;
  integrationID: string;
  modelName: string;
  diffType: DiffCategoryTypes | null;
  collapseUnchangedLines?: boolean;
  stagedBlueprintVersionID: string;
  publishedBlueprintVersionID: string;
  modelResult: DiffExecutionModelResult;
}

const ModelDiffView = memo(
  ({
    diffRunID,
    candidateTitle = "Staged",
    integrationID,
    modelName,
    diffType,
    collapseUnchangedLines = false,
    stagedBlueprintVersionID,
    publishedBlueprintVersionID,
    modelResult,
  }: ModelDiffViewProps) => {
    // state for open/close of accordion
    const [isOpen, setIsOpen] = useState(false);

    // hook to get common model diff results; the backend already filters to just this
    // category server-side when diffType is set, so results never need client-side filtering
    const { lastKey, results, totalModelCount, loading, loadMore } = useGetCommonModelDiffResult({
      commonModelDiffRunExecutionID: diffRunID,
      modelName,
      diffCategory: diffType || undefined,
      modelResult: modelResult,
    });

    // hook to keep content rendered for 2000ms after isOpen becomes false
    const shouldRenderContent = useDelayedUnmount(isOpen, 2000);

    // refs for sentinel element
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    // callback functions for accordion toggle and loading more data
    const handleSetExpanded = useCallback(
      (expanded: boolean) => {
        if (totalModelCount && totalModelCount > 0) {
          setIsOpen(expanded);
        }
      },
      [totalModelCount]
    );

    // only fire loadMore if we actually have more to fetch (e.g. do calculation with total model count and the number of results we have)
    const memoizedLoadMore = useCallback(() => {
      if (lastKey && totalModelCount != null && results.length < totalModelCount) {
        loadMore();
      }
    }, [lastKey, loadMore, results.length, totalModelCount]);

    // useEffect to auto‑close if nothing to show
    useEffect(() => {
      if (totalModelCount === 0 && isOpen) {
        setIsOpen(false);
      }
    }, [totalModelCount, isOpen]);

    // useEffect to handle infinite scroll - preloads when 300px from sentinel
    useEffect(() => {
      if (!shouldRenderContent || loading) return;
      const rootEl = scrollContainerRef.current;
      if (!rootEl || !loadMoreRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            memoizedLoadMore();
          }
        },
        {
          root: rootEl,
          rootMargin: "0px 0px 300px 0px",
          threshold: 0,
        }
      );

      observer.observe(loadMoreRef.current);
      return () => {
        observer.disconnect();
      };
    }, [shouldRenderContent, loading, memoizedLoadMore]);

    // const derived from state
    const chevronSize = totalModelCount && totalModelCount > 0 ? 16 : 0;

    return (
      <Accordion
        titleClassName={clsx("p-5", {
          "cursor-default !important": totalModelCount === 0,
        })}
        chevronSize={chevronSize}
        expanded={isOpen}
        setExpanded={handleSetExpanded}
        title={
          <div className="flex items-center justify-center">
            <Text as="div" variant="h5" className="mr-2">
              {modelName}
            </Text>
            {totalModelCount !== null ? (
              <Badge className="flex items-center justify-center">{totalModelCount}</Badge>
            ) : (
              <ContentLoader
                speed={0.7}
                width={23}
                height={20}
                viewBox="0 0 22 20"
                backgroundColor="#f0f0f0"
                foregroundColor="#e0e0e0"
                className="rounded"
              >
                <rect x="0" y="0" rx="4" ry="4" width="22" height="20" />
              </ContentLoader>
            )}
          </div>
        }
      >
        {shouldRenderContent && (
          <>
            <hr className="text-gray-40 h-[0.5px] my-0" />
            <div ref={scrollContainerRef} className="p-5 max-h-[736px] overflow-y-auto">
              <div className="space-y-4">
                {results.map((result, index) => (
                  <IndividualView
                    key={index}
                    result={result}
                    index={index}
                    candidateTitle={candidateTitle}
                    collapseUnchangedLines={collapseUnchangedLines}
                    stagedBlueprintVersionID={stagedBlueprintVersionID}
                    publishedBlueprintVersionID={publishedBlueprintVersionID}
                    integrationID={integrationID}
                  />
                ))}
                {loading && (
                  <div className="flex items-center justify-center">
                    <Spinner />
                  </div>
                )}
              </div>

              {/* sentinel, outside of space-y, for IntersectionObserver */}
              <div ref={loadMoreRef} />
            </div>
          </>
        )}
      </Accordion>
    );
  }
);

export default ModelDiffView;
